import { useMemo } from 'react'

import type { EChartsOption, LineSeriesOption } from 'echarts'

import { type PeriodWindow, buildMidnightMarkLineSeries, makeTimeAxis } from '@/components/charts/history/timeAxis'
import { ALERT_MARKER_TOP_PX, buildAlertMarkerSeries } from '@/insights/assets/historyGraph/alertMarkerSeries'
import { STATUS_AXIS_PX } from '@/insights/assets/historyGraph/constants'
import type { AlertMarker } from '@/insights/assets/historyGraph/types'
import { kphToMph, SpeedUnit } from '@/utils/conversions'
import { formatHistoryDateTime } from '@/utils/formatDate'

import { CANVAS_LEFT, CANVAS_RIGHT, CANVAS_TOP, AVERAGE_SPEED_COLOR, MAXIMUM_SPEED_COLOR } from './constants'
import type { ReeferSpeedSample } from './types'

interface SpeedPoint {
  value: [number, number]
  name: string
}

const toPoints = (samples: ReeferSpeedSample[], type: ReeferSpeedSample['type'], unit: SpeedUnit): SpeedPoint[] =>
  samples
    .filter((s) => s.type === type && s.speedKmsHr != null)
    .map((s) => ({
      value: [
        new Date(s.sampleTime).getTime(),
        unit === 'mph' ? kphToMph(s.speedKmsHr as number) : (s.speedKmsHr as number),
      ] as [number, number],
      name: s.location ?? '',
    }))
    .sort((a, b) => a.value[0] - b.value[0])

export function useSpeedChartOptions(
  samples: ReeferSpeedSample[] | undefined,
  periodWindow: PeriodWindow,
  zone: string,
  unit: SpeedUnit,
  alertMarkers?: AlertMarker
): EChartsOption {
  return useMemo(() => {
    const speedSeries: LineSeriesOption[] = (['Average Speed', 'Maximum Speed'] as const).map((type) => {
      const color = type === 'Average Speed' ? AVERAGE_SPEED_COLOR : MAXIMUM_SPEED_COLOR
      return {
        type: 'line',
        name: type,
        data: toPoints(samples ?? [], type, unit),
        showSymbol: true,
        symbol: 'circle',
        symbolSize: 5,
        connectNulls: false,
        lineStyle: { width: 2, color },
        itemStyle: { color },
        z: 3,

        tooltip: {
          formatter: (params) => {
            const point = params as unknown as { value: [number, number]; name: string }
            const [time, speed] = point.value
            const lines = [`<b>${type}</b>: ${Number(speed.toFixed(1))} ${unit}`, formatHistoryDateTime(time, zone)]
            if (point.name) lines.push(point.name)
            return lines.join('<br/>')
          },
        },
      }
    })

    const midnights = buildMidnightMarkLineSeries(periodWindow, zone, 0, 0)
    const series: LineSeriesOption[] = [...speedSeries]
    if (midnights) series.push(midnights)

    const alertMarkerSeries = buildAlertMarkerSeries(alertMarkers, periodWindow, zone)
    if (alertMarkerSeries) series.push(alertMarkerSeries)

    return {
      animation: false,
      grid: [
        {
          left: CANVAS_LEFT,
          right: CANVAS_RIGHT,
          top: alertMarkerSeries ? ALERT_MARKER_TOP_PX : CANVAS_TOP,
          bottom: STATUS_AXIS_PX,
        },
      ],
      xAxis: [makeTimeAxis({ gridIndex: 0, showLabel: true, periodWindow, zone })],
      legend: {
        data: ['Average Speed', 'Maximum Speed'],
        selected: {
          'Average Speed': true,
          'Maximum Speed': true,
        },
        align: 'left',
        orient: 'vertical',
        left: 0,
        top: 'top',
      },
      yAxis: [
        {
          type: 'value',
          gridIndex: 0,
          min: function (value) {
            // value.min is the lowest data point
            return Math.floor(value.min - value.min * 0.1)
          },
          max: function (value) {
            // value.max is the highest data point
            return Math.ceil(value.max + value.max * 0.1)
          },
          interval: 25,
          axisLabel: { formatter: '{value}' },
          name: `Speed ${unit}`,
          nameLocation: 'middle',
          nameGap: 42,
          splitLine: { show: true, lineStyle: { color: '#d1d5db', opacity: 0.5 } },
        },
      ],
      tooltip: { trigger: 'item', confine: true },
      dataZoom: [{ type: 'inside', xAxisIndex: [0], filterMode: 'none', minSpan: 0, maxSpan: 100, throttle: 30 }],
      series,
    } as EChartsOption
  }, [samples, periodWindow, zone, unit, alertMarkers])
}
